import React, { useState } from 'react';
import { bookings } from '../../api';
import './Bookings.css';

const LESSON_DURATIONS = [
  { value: 30, label: '30 minutes' },
  { value: 60, label: '60 minutes (1 hour)' },
  { value: 90, label: '90 minutes' },
  { value: 120, label: '120 minutes (2 hours)' }
];

const EXPIRY_PRESETS = [
  { value: 'none', label: 'No expiry' },
  { value: '3', label: '3 months' },
  { value: '6', label: '6 months' },
  { value: '12', label: '1 year' },
  { value: '24', label: '2 years' },
  { value: 'custom', label: 'Custom date' }
];

// Local calendar date, not UTC - Date.toISOString() is UTC-based, so near
// midnight in any timezone behind UTC it names YESTERDAY as "today" (e.g.
// 11pm Sep 17 in a UTC-8 zone is already Sep 18 UTC... the reverse case,
// UTC ahead of local, undershoots instead - either way the coach's own
// calendar date is what belongs here, not UTC's).
function todayDateString() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Adds `months` calendar months to a YYYY-MM-DD date string, returned in
// the same format. Uses setMonth (not a fixed day-count) so "3 months"
// from any given day lands on that same day-of-month 3 months later,
// rather than a slightly-off approximation - e.g. Jan 31 + 1 month
// correctly rolls to the last valid day of February instead of March 3.
//
// Parses/reassembles the date via the (year, monthIndex, day) constructor
// and local getters throughout, never new Date(dateStr) or toISOString():
// a bare "YYYY-MM-DD" string parses as UTC midnight, but .getDate() etc.
// read local time - mixing the two shifts the result by a day in any
// timezone behind UTC.
function addMonths(dateStr, months) {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, 1);
  date.setMonth(date.getMonth() + months);
  const daysInTargetMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  date.setDate(Math.min(day, daysInTargetMonth));
  const resultYear = date.getFullYear();
  const resultMonth = String(date.getMonth() + 1).padStart(2, '0');
  const resultDay = String(date.getDate()).padStart(2, '0');
  return `${resultYear}-${resultMonth}-${resultDay}`;
}

const SESSION_TYPES = [
  { value: 'custom', label: 'Custom' },
  { value: 'technique', label: '🎾 Technique Training' },
  { value: 'match-play', label: '🏆 Match Play' },
  { value: 'drill-practice', label: '🎯 Drill Practice' },
  { value: 'match-prep', label: '📋 Match Prep' },
  { value: 'mental', label: '🧠 Mental Training' }
];

const FALLBACK_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
  'Australia/Sydney'
];

// Every IANA timezone the browser knows about (~400), not just a handful -
// Intl.supportedValuesOf is standard in every browser this app targets
// (Safari 16.4+, Chrome/Edge 99+), but fall back to a short list rather
// than crash on something older.
const ALL_TIMEZONES = (() => {
  try {
    if (typeof Intl.supportedValuesOf === 'function') {
      return Intl.supportedValuesOf('timeZone').sort();
    }
  } catch (e) {
    // fall through to fallback list
  }
  return FALLBACK_TIMEZONES;
})();

const DEFAULT_TIMEZONE = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
  } catch (e) {
    return 'America/New_York';
  }
})();

export default function CreateCoachingPlanModal({ isOpen, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sessionType: 'custom',
    tags: '',
    maxBookings: 1,
    sessionCount: 10,
    sessionDurationMinutes: 60,
    schedulingMode: 'flexible',
    sessionFrequency: 'weekly',
    daysOfWeek: [],
    timeOfDay: '14:00',
    timezone: DEFAULT_TIMEZONE,
    startDate: todayDateString(),
    endDate: '',
    expiryPreset: 'none',
    totalPrice: ''
  });

  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState('');

  // Price per session is always derived from total price / session count -
  // not its own input, so it can never disagree with the total.
  const pricePerSession = (() => {
    const total = parseFloat(formData.totalPrice);
    const count = parseInt(formData.sessionCount);
    if (!total || !count) return null;
    return total / count;
  })();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Flexible-mode expiry: either a duration preset (auto-calculated from
  // the start date) or a custom picked date. Re-run whenever the preset or
  // the start date itself changes, so editing the start date afterward
  // keeps a preset-based expiry correct instead of leaving it pinned to
  // whatever it was calculated from originally.
  const handleExpiryPresetChange = (e) => {
    const preset = e.target.value;
    setFormData(prev => ({
      ...prev,
      expiryPreset: preset,
      endDate: preset !== 'none' && preset !== 'custom' && prev.startDate
        ? addMonths(prev.startDate, parseInt(preset, 10))
        : (preset === 'none' ? '' : prev.endDate)
    }));
  };

  const handleStartDateChange = (e) => {
    const startDate = e.target.value;
    setFormData(prev => ({
      ...prev,
      startDate,
      endDate: prev.expiryPreset !== 'none' && prev.expiryPreset !== 'custom' && startDate
        ? addMonths(startDate, parseInt(prev.expiryPreset, 10))
        : prev.endDate
    }));
  };

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter(d => d !== day)
        : [...prev.daysOfWeek, day].sort((a, b) => a - b)
    }));
  };

  const calculateEndDate = () => {
    if (!formData.startDate || formData.daysOfWeek.length === 0) return null;

    const start = new Date(formData.startDate);
    let count = 0;
    const frequencyDays = formData.sessionFrequency === 'biweekly' ? 14 : 7;

    while (count < formData.sessionCount) {
      if (formData.daysOfWeek.includes(start.getDay())) {
        count++;
        if (count === formData.sessionCount) break;
      }
      start.setDate(start.getDate() + 1);

      // Safety check
      if (start.getTime() - new Date(formData.startDate).getTime() > 365 * 24 * 60 * 60 * 1000) {
        return null;
      }
    }

    return start.toISOString().split('T')[0];
  };

  const generatePreview = () => {
    try {
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const selectedDays = formData.daysOfWeek.map(d => dayNames[d]).join(', ');
      const endDate = calculateEndDate();

      const isFlexible = formData.schedulingMode === 'flexible';

      let previewText = `📦 Coaching Plan: ${formData.name}\n`;
      previewText += `\n📝 Description:\n${formData.description || '(No description)'}\n`;
      previewText += `\n📊 Plan Details:\n`;
      previewText += `   Sessions: ${formData.sessionCount}\n`;
      previewText += `   Duration: ${formData.sessionDurationMinutes} minutes each\n`;
      if (isFlexible) {
        previewText += `\n🕐 Scheduling: Flexible\n`;
        previewText += `   Each session is booked to its own date/time after purchase.\n`;
        if (formData.endDate) {
          previewText += `   Pack expires: ${new Date(formData.endDate).toLocaleDateString()}\n`;
        } else {
          previewText += `   Pack expiry: none\n`;
        }
      } else {
        previewText += `   Frequency: ${formData.sessionFrequency === 'biweekly' ? 'Every 2 weeks' : 'Every week'}\n`;
        previewText += `   Days: ${selectedDays}\n`;
        previewText += `\n🕐 Schedule:\n`;
        previewText += `   Time: ${formData.timeOfDay}\n`;
        previewText += `   Timezone: ${formData.timezone}\n`;
        previewText += `   Start: ${new Date(formData.startDate).toLocaleDateString()}\n`;
        if (endDate) {
          previewText += `   End: ${new Date(endDate).toLocaleDateString()}\n`;
        }
      }

      if (formData.totalPrice) {
        previewText += `\n💰 Pricing:\n`;
        previewText += `   Total: $${parseFloat(formData.totalPrice).toFixed(2)}\n`;
        if (pricePerSession !== null) {
          previewText += `   Per Session: $${pricePerSession.toFixed(2)} (${formData.totalPrice} ÷ ${formData.sessionCount} sessions)\n`;
        }
      }

      setPreview(previewText);
    } catch (err) {
      console.error('Error generating preview:', err);
      setPreview('⚠️ Error generating preview');
    }
  };

  const handleNext = () => {
    setError(null);

    if (step === 1) {
      if (!formData.name.trim()) {
        setError('Plan name is required');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (formData.schedulingMode !== 'flexible') {
        if (formData.daysOfWeek.length === 0) {
          setError('Select at least one day of the week');
          return;
        }
        if (!formData.startDate) {
          setError('Start date is required');
          return;
        }
      }
      generatePreview();
      setStep(3);
    } else if (step === 3) {
      setStep(4);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const isFlexible = formData.schedulingMode === 'flexible';
      const endDate = isFlexible
        ? (formData.endDate || null)
        : calculateEndDate();
      if (!isFlexible && !endDate) {
        throw new Error('Could not calculate plan end date');
      }

      const tags = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      const planData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        sessionType: formData.sessionType,
        tags,
        maxBookings: Math.min(parseInt(formData.maxBookings) || 1, 4),
        sessionCount: parseInt(formData.sessionCount),
        sessionDurationMinutes: parseInt(formData.sessionDurationMinutes),
        schedulingMode: formData.schedulingMode,
        sessionFrequency: formData.sessionFrequency,
        daysOfWeek: isFlexible ? [] : formData.daysOfWeek,
        timeOfDay: isFlexible ? null : formData.timeOfDay,
        timezone: formData.timezone,
        startDate: isFlexible
          ? (formData.startDate ? new Date(formData.startDate).toISOString() : null)
          : new Date(formData.startDate).toISOString(),
        endDate: endDate ? new Date(endDate).toISOString() : null,
        totalPrice: formData.totalPrice ? parseFloat(formData.totalPrice) : null,
        pricePerSession
      };

      await bookings.createCoachingPlan(planData);

      // Reset form
      setFormData({
        name: '',
        description: '',
        sessionType: 'custom',
        tags: '',
        maxBookings: 1,
        sessionCount: 10,
        sessionDurationMinutes: 60,
        schedulingMode: 'flexible',
        sessionFrequency: 'weekly',
        daysOfWeek: [],
        timeOfDay: '14:00',
        timezone: DEFAULT_TIMEZONE,
        startDate: todayDateString(),
        endDate: '',
        expiryPreset: 'none',
        totalPrice: ''
      });
      setStep(1);

      if (onSuccess) {
        setTimeout(() => onSuccess(), 500);
      }
      setTimeout(() => onClose(), 1000);
    } catch (err) {
      console.error('❌ Error creating coaching plan:', err);
      setError(err.response?.data?.message || 'Failed to create coaching plan');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="booking-modal-overlay" onClick={onClose}>
      <div className="booking-modal-content" onClick={e => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="booking-modal-header">
          <h2>📦 Create Coaching Plan (Step {step}/4)</h2>
          <button className="booking-modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="booking-form">
          {error && <div className="booking-error">{error}</div>}

          {/* STEP 1: BASIC INFO */}
          {step === 1 && (
            <>
              <div className="booking-form-group">
                <label htmlFor="name">Plan Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., 4-Week Forehand Intensive"
                  maxLength="100"
                  required
                />
              </div>

              <div className="booking-form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="What will players learn? What should they prepare?"
                  maxLength="500"
                  rows="4"
                />
              </div>

              <div className="booking-form-group">
                <label htmlFor="sessionType">Session Type</label>
                <select
                  id="sessionType"
                  name="sessionType"
                  value={formData.sessionType}
                  onChange={handleInputChange}
                >
                  {SESSION_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="booking-form-group">
                <label htmlFor="tags">Tags (optional, comma-separated)</label>
                <input
                  type="text"
                  id="tags"
                  name="tags"
                  value={formData.tags}
                  onChange={handleInputChange}
                  placeholder="e.g., forehand, advanced, match-prep"
                  maxLength="100"
                />
              </div>

              <div className="booking-form-group">
                <label htmlFor="maxBookings">Max Participants (1-4)</label>
                <select
                  id="maxBookings"
                  name="maxBookings"
                  value={formData.maxBookings}
                  onChange={handleInputChange}
                >
                  <option value="1">1 person</option>
                  <option value="2">2 people</option>
                  <option value="3">3 people</option>
                  <option value="4">4 people</option>
                </select>
              </div>
            </>
          )}

          {/* STEP 2: SCHEDULE */}
          {step === 2 && (
            <>
              <div className="booking-form-group">
                <label>Sessions in Plan *</label>
                <input
                  type="number"
                  name="sessionCount"
                  min="2"
                  max="20"
                  value={formData.sessionCount}
                  onChange={handleInputChange}
                  placeholder="e.g., 10"
                />
                <small style={{ color: '#7f8c8d', marginTop: '4px', display: 'block' }}>
                  Between 2 and 20 sessions
                </small>
              </div>

              <div className="booking-form-group">
                <label htmlFor="sessionDurationMinutes">Session Duration *</label>
                <select
                  id="sessionDurationMinutes"
                  name="sessionDurationMinutes"
                  value={formData.sessionDurationMinutes}
                  onChange={handleInputChange}
                  required
                >
                  {LESSON_DURATIONS.map(dur => (
                    <option key={dur.value} value={dur.value}>
                      {dur.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="booking-form-group">
                <label htmlFor="schedulingMode">Scheduling *</label>
                <select
                  id="schedulingMode"
                  name="schedulingMode"
                  value={formData.schedulingMode}
                  onChange={handleInputChange}
                >
                  <option value="flexible">Flexible — schedule each session individually</option>
                  <option value="weekly">Fixed cadence — every week</option>
                  <option value="biweekly">Fixed cadence — every 2 weeks</option>
                </select>
                <small style={{ color: '#7f8c8d', marginTop: '4px', display: 'block' }}>
                  {formData.schedulingMode === 'flexible'
                    ? 'The pack is bought as a set of session credits; you and the student pick a date/time for each one afterward.'
                    : 'Sessions are seeded on this cadence. Each can still be rescheduled later.'}
                </small>
              </div>

              {formData.schedulingMode === 'flexible' ? (
                <>
                  <div className="booking-form-group">
                    <label htmlFor="startDate">Start Date</label>
                    <input
                      type="date"
                      id="startDate"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleStartDateChange}
                    />
                    <small style={{ color: '#7f8c8d', marginTop: '4px', display: 'block' }}>
                      Defaults to today - change it if the pack shouldn't start yet.
                    </small>
                  </div>

                  <div className="booking-form-group">
                    <label htmlFor="expiryPreset">Pack Expires</label>
                    <select
                      id="expiryPreset"
                      name="expiryPreset"
                      value={formData.expiryPreset}
                      onChange={handleExpiryPresetChange}
                    >
                      {EXPIRY_PRESETS.map(preset => (
                        <option key={preset.value} value={preset.value}>
                          {preset.label}
                        </option>
                      ))}
                    </select>
                    <small style={{ color: '#7f8c8d', marginTop: '4px', display: 'block' }}>
                      After this date, unused sessions are forfeited.
                    </small>
                  </div>

                  {formData.expiryPreset === 'custom' && (
                    <div className="booking-form-group">
                      <label htmlFor="endDate">Expiry Date</label>
                      <input
                        type="date"
                        id="endDate"
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleInputChange}
                        min={formData.startDate || todayDateString()}
                      />
                    </div>
                  )}

                  {formData.expiryPreset !== 'none' && formData.expiryPreset !== 'custom' && formData.endDate && (
                    <div className="booking-form-group">
                      <small style={{ color: '#7f8c8d', display: 'block' }}>
                        Expires {new Date(formData.endDate).toLocaleDateString()}
                      </small>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="booking-form-group">
                    <label>Days of Week (select at least one) *</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '16px' }}>
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => handleDayToggle(idx)}
                          style={{
                            padding: '12px',
                            borderRadius: '6px',
                            border: formData.daysOfWeek.includes(idx) ? '2px solid #3498db' : '1px solid #bdc3c7',
                            background: formData.daysOfWeek.includes(idx) ? '#e8f4f8' : '#fff',
                            cursor: 'pointer',
                            fontWeight: formData.daysOfWeek.includes(idx) ? 'bold' : 'normal',
                            color: formData.daysOfWeek.includes(idx) ? '#3498db' : '#2c3e50'
                          }}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="booking-form-group">
                    <label htmlFor="sessionFrequency">Frequency *</label>
                    <select
                      id="sessionFrequency"
                      name="sessionFrequency"
                      value={formData.sessionFrequency}
                      onChange={handleInputChange}
                    >
                      <option value="weekly">Every week</option>
                      <option value="biweekly">Every 2 weeks</option>
                    </select>
                  </div>

                  <div className="booking-form-group">
                    <label htmlFor="startDate">Start Date *</label>
                    <input
                      type="date"
                      id="startDate"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </>
              )}
            </>
          )}

          {/* STEP 3: TIME & PRICING */}
          {step === 3 && (
            <>
              {formData.schedulingMode !== 'flexible' && (
                <div className="booking-form-group">
                  <label htmlFor="timeOfDay">Session Start Time *</label>
                  <input
                    type="time"
                    id="timeOfDay"
                    name="timeOfDay"
                    value={formData.timeOfDay}
                    onChange={handleInputChange}
                  />
                </div>
              )}

              <div className="booking-form-group">
                <label htmlFor="timezone">Timezone *</label>
                <select
                  id="timezone"
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleInputChange}
                  required
                >
                  {ALL_TIMEZONES.map(tz => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>

              <div className="booking-form-group">
                <label htmlFor="totalPrice">Total Price (optional)</label>
                <input
                  type="number"
                  id="totalPrice"
                  name="totalPrice"
                  value={formData.totalPrice}
                  onChange={handleInputChange}
                  placeholder="e.g., 199.99"
                  step="0.01"
                  min="0"
                />
                <small style={{ color: '#7f8c8d', marginTop: '4px', display: 'block' }}>
                  Price for entire plan
                </small>
              </div>

              <div className="booking-form-group">
                <label>Price Per Session</label>
                <div style={{
                  padding: '10px 12px',
                  borderRadius: '6px',
                  background: '#f8f9fa',
                  border: '1px solid #dcdcdc',
                  color: pricePerSession !== null ? '#2c3e50' : '#95a5a6',
                  fontWeight: pricePerSession !== null ? '600' : '400'
                }}>
                  {pricePerSession !== null
                    ? `$${pricePerSession.toFixed(2)}`
                    : 'Enter a total price and session count to calculate'}
                </div>
                <small style={{ color: '#7f8c8d', marginTop: '4px', display: 'block' }}>
                  Total price ÷ {formData.sessionCount || '?'} sessions - calculated automatically
                </small>
              </div>
            </>
          )}

          {/* STEP 4: REVIEW */}
          {step === 4 && (
            <div style={{
              padding: '16px',
              background: '#f0f7ff',
              borderRadius: '8px',
              borderLeft: '4px solid #3498db',
              whiteSpace: 'pre-line',
              fontSize: '13px',
              color: '#2c3e50',
              marginBottom: '16px',
              fontFamily: 'monospace',
              maxHeight: '400px',
              overflowY: 'auto'
            }}>
              {preview}
            </div>
          )}

          <div className="booking-form-actions">
            {step > 1 && (
              <button type="button" className="booking-btn-secondary" onClick={handleBack}>
                ← Back
              </button>
            )}
            {step < 4 && (
              <button type="button" className="booking-btn-primary" onClick={handleNext}>
                Next →
              </button>
            )}
            {step === 4 && (
              <>
                <button type="button" className="booking-btn-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="booking-btn-primary"
                  disabled={submitting}
                >
                  {submitting ? '⏳ Creating...' : '✅ Create Plan'}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
